import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { cn } from '../../lib/utils';

interface NavItem {
  path: string;
  label: string;
  section?: string;
  icon?: string;
  badge?: string;
  /** Admin-only entry (epic 103 D2/D8) — hidden for every other role. */
  adminOnly?: boolean;
}

const navItems: NavItem[] = [
  { path: '/', label: 'Dashboard', section: 'Overview', icon: 'grid' },
  { path: '/campaigns', label: 'Campaigns', section: 'Campaigns', icon: 'flow' },
  // Read-only list of every custom page by sequence and spot (task 071-01).
  { path: '/page-order', label: 'Page Order', section: 'Campaigns', icon: 'list' },
  // Route Rules moved from the Advertisers section — a rule binds to the
  // receiving campaign (HF-035); operator 2026-09-16 (HF-036).
  { path: '/route-rules', label: 'Route Rules', section: 'Campaigns', icon: 'flow' },
  // Advertiser adapters (epic 006) — the lead-integration surfaces live in the
  // Advertisers section (epic 053 unified the visible vocabulary).
  { path: '/advertisers', label: 'Advertisers', section: 'Advertisers', icon: 'people' },
  {
    path: '/advertiser-credentials',
    label: 'Advertiser Credentials',
    section: 'Advertisers',
    icon: 'shield',
  },
  {
    path: '/advertiser-endpoints',
    label: 'Advertiser Endpoints',
    section: 'Advertisers',
    icon: 'flow',
  },
  // CTO group — Sites + Promo types + the EmailOversight allow-list (Chelsie:
  // "this should be part of the CTO link section", admin-requirements.md
  // §3.7/§2.9; allow-list: Q93, epic 024 task 07).
  { path: '/sites', label: 'Sites', section: 'CTO', icon: 'layers' },
  { path: '/promo-types', label: 'Promo Types', section: 'CTO', icon: 'tag' },
  // CTO link directory (task 064-01, Chelsie QA-07): every CTO link across
  // every campaign, filterable by traffic source — distinct from the
  // per-campaign links section on the campaign form.
  { path: '/cto-links', label: 'CTO Links', section: 'CTO', icon: 'flow' },
  { path: '/allow-list', label: 'Allow List', section: 'CTO', icon: 'check-circle' },
  // CTO approved domains (epic 108 task 02) — admin-only: the raw comma
  // string's one editor, no rows (Chelsie's operator ruling, D2).
  {
    path: '/cto-approved-domains',
    label: 'Approved Domains',
    section: 'CTO',
    icon: 'check-circle',
    adminOnly: true,
  },
  { path: '/suppression', label: 'Suppression Files', section: 'Operations', icon: 'shield' },
  { path: '/leads', label: 'Leads', section: 'Operations', icon: 'list' },
  { path: '/test-runs', label: 'Test Runs', section: 'Operations', icon: 'check-circle' },
  // User management (epic 103 D8) — admins only: the one surface roles gate.
  { path: '/users', label: 'Users', section: 'Operations', icon: 'people', adminOnly: true },
  { path: '/reports/co-op', label: 'Reporting', section: 'Reports', icon: 'list' },
  { path: '/distribution', label: 'Distribution', section: 'Operations', icon: 'flow' },
  // NOTE: Publishers screen exists but is demoted from primary nav (per admin-requirements.md §2.5)
  // Route /publishers remains reachable for entity CRUD.
];

function Icon({ name }: { name: string }) {
  const common = {
    width: 16,
    height: 16,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  const paths: Record<string, JSX.Element> = {
    grid: (
      <>
        <rect x="2" y="2" width="5" height="5" rx="1" />
        <rect x="9" y="2" width="5" height="5" rx="1" />
        <rect x="2" y="9" width="5" height="5" rx="1" />
        <rect x="9" y="9" width="5" height="5" rx="1" />
      </>
    ),
    flow: (
      <>
        <circle cx="4" cy="4" r="2" />
        <circle cx="12" cy="8" r="2" />
        <circle cx="4" cy="12" r="2" />
        <path d="M6 4h2.5a1.5 1.5 0 0 1 1.5 1.5V6M6 12h2.5A1.5 1.5 0 0 0 10 10.5V10" />
      </>
    ),
    shield: (
      <>
        <path d="M8 2l5 2v4c0 3-2.2 5-5 6-2.8-1-5-3-5-6V4l5-2Z" />
        <path d="M6 8l1.5 1.5L10.5 6.5" />
      </>
    ),
    people: (
      <>
        <circle cx="6" cy="5" r="2.2" />
        <path d="M2 13c0-2.2 1.8-4 4-4s4 1.8 4 4" />
        <circle cx="11.5" cy="5.5" r="1.6" />
        <path d="M14 13c0-1.7-1.2-3-2.5-3" />
      </>
    ),
    layers: (
      <>
        <path d="M2 5 8 2 14 5 8 8Z" />
        <path d="M2 10 8 13 14 10" />
        <path d="M2 15 8 18 14 15" />
      </>
    ),
    list: (
      <>
        <path d="M8 6h6" />
        <path d="M8 12h6" />
        <path d="M8 18h6" />
        <path d="M2 6h3" />
        <path d="M2 12h3" />
        <path d="M2 18h3" />
      </>
    ),
    tag: (
      <>
        <path d="M2 2h5l7 7-5 5-7-7Z" />
        <circle cx="5.5" cy="5.5" r="1.2" />
      </>
    ),
    'check-circle': (
      <>
        <circle cx="8" cy="8" r="6" />
        <path d="M5.5 8l1.8 1.8L10.8 6.3" />
      </>
    ),
  };
  return <svg {...common}>{paths[name]}</svg>;
}

/** Avatar initials from the display name ("Dev Path" → "DP"). */
function initialsOf(displayName: string): string {
  const words = displayName.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}

interface SidebarProps {
  onNavigate?: () => void;
}

export function Sidebar({ onNavigate }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  // Role filter (epic 103 D2): the ONLY role-aware nav rule — admin-only
  // entries drop out for everyone else; no permission framework.
  const visibleNavItems = navItems.filter(
    (item) => item.adminOnly !== true || user?.role === 'admin'
  );

  // The user block doubles as the account-menu toggle (HF-144, QA #135).
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const userMenuToggleRef = useRef<HTMLButtonElement | null>(null);

  const handleLogout = async () => {
    // logout() never throws and always clears the local session, so the
    // shell always lands on the login screen — but it reports whether the
    // api confirmed the revocation, and the failed case is stated on /login
    // instead of passing as a clean sign-out (QA round 18 #139 ride-along).
    const { revoked } = await logout();
    navigate('/login', { replace: true, state: { logoutRevoked: revoked } });
  };

  // Menu dismissal while open (HF-144): a mousedown outside the user block
  // and the Escape key close it, Escape returning focus to the toggle. The
  // menu also folds when the session ends so it can never outlive the
  // signed-in state it belongs to.
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDocMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (target instanceof Node && userMenuRef.current?.contains(target) !== true) {
        setUserMenuOpen(false);
      }
    };
    const onDocKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setUserMenuOpen(false);
        userMenuToggleRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onDocKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onDocKeyDown);
    };
  }, [userMenuOpen]);

  useEffect(() => {
    if (user === null) setUserMenuOpen(false);
  }, [user]);

  const groupedItems = visibleNavItems.reduce<Record<string, NavItem[]>>((acc, item) => {
    const section = item.section || 'Other';
    if (!acc[section]) acc[section] = [];
    acc[section].push(item);
    return acc;
  }, {});

  return (
    <aside id="admin-sidebar" className="flex w-[248px] shrink-0 flex-col bg-rail text-rail-text">
      {/* brand */}
      <div className="flex items-center gap-2.5 px-5 py-5">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-accent font-display text-[15px] font-bold text-white">
          T
        </div>
        <div className="leading-tight">
          <div className="font-display text-[15px] font-semibold text-white">TradingTips</div>
          <div className="font-mono text-xs uppercase tracking-[0.16em] text-rail-muted">
            Lead Ops Console
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {Object.entries(groupedItems).map(([section, items]) => (
          <div key={section} className="mb-5">
            <div className="px-3 pb-1.5">
              <div className="font-mono text-xs font-medium uppercase tracking-[0.14em] text-rail-muted">
                {section}
              </div>
            </div>
            <div className="space-y-0.5">
              {items.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={onNavigate}
                    className={cn(
                      'group flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors',
                      active
                        ? 'bg-accent/15 text-white shadow-[inset_2px_0_0_var(--color-accent)]'
                        : 'text-rail-text hover:bg-rail-soft hover:text-white'
                    )}
                  >
                    <span
                      className={cn(
                        active ? 'text-accent' : 'text-rail-muted group-hover:text-rail-text'
                      )}
                    >
                      {item.icon && <Icon name={item.icon} />}
                    </span>
                    <span className="flex-1 text-left">{item.label}</span>
                    {item.badge && (
                      <span className="rounded-full bg-rail-line px-1.5 py-0.5 font-mono text-xs text-rail-muted">
                        {item.badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-rail-line px-5 py-3.5">
        {/* Signed-in user block (epic 076 task 03): the session's own email,
            replacing the prototype's hardcoded persona. Since HF-144 (QA #135)
            it is also the account-menu toggle — closed it looks the same. */}
        <div className="relative" ref={userMenuRef}>
          <button
            type="button"
            ref={userMenuToggleRef}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            onClick={() => {
              setUserMenuOpen((open) => !open);
            }}
            className="flex w-full items-center gap-2.5 rounded-lg text-left transition-colors hover:bg-rail-soft"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rail-line font-mono text-xs text-rail-text">
              {user === null ? '?' : initialsOf(user.displayName)}
            </span>
            <span className="min-w-0 leading-tight">
              <span className="block truncate text-xs font-medium text-white">
                {user === null ? 'Signed out' : user.email}
              </span>
              <span className="block text-xs text-rail-muted">Admin</span>
            </span>
          </button>

          {userMenuOpen && (
            <div
              role="menu"
              aria-label="Account"
              className="absolute bottom-full left-0 right-0 z-10 mb-1.5 rounded-lg border border-rail-line bg-rail p-1 shadow-lg"
            >
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/change-password');
                  onNavigate?.();
                }}
                className="flex w-full items-center rounded-md px-3 py-2 text-[13px] text-rail-text transition-colors hover:bg-rail-soft hover:text-white"
              >
                Change password
              </button>
            </div>
          )}
        </div>

        <div className="mt-3 flex items-center justify-between gap-1.5 rounded-md bg-rail-line/60 px-2 py-1 font-mono text-xs tracking-wide text-rail-muted">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
            v0.1.0 — Stage A Admin
          </span>
          <button
            type="button"
            onClick={() => {
              void handleLogout();
            }}
            className="shrink-0 text-rail-muted transition-colors hover:text-white"
          >
            Log out
          </button>
        </div>
      </div>
    </aside>
  );
}
